import { useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import { createDeviceListRequest } from "../utils";
import type { Coverage } from "@adlywise/shared";
import type { DeviceAggregateRow, DeviceAggregatesResponse } from "../types";
import { deviceAggregatesOf } from "../coverage";

type UseDeviceAggregatesParams = {
  deviceIds: string[];
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
};

type UseDeviceAggregatesResult = {
  aggregates: Record<string, DeviceAggregateRow>;
  coverage: Coverage | null;
  isLoading: boolean;
};

export const useDeviceAggregates = ({
  deviceIds,
  selectedRange,
  appliedFilters,
}: UseDeviceAggregatesParams): UseDeviceAggregatesResult => {
  const { performRequest } = useApp();
  const [aggregates, setAggregates] = useState<
    Record<string, DeviceAggregateRow>
  >({});
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const cacheKey = deviceIds.length > 0 ? deviceIds.join(",") : "";

  useEffect(
    () =>
      loadDeviceAggregates(
        { deviceIds, selectedRange, appliedFilters, performRequest, abortRef },
        { setAggregates, setCoverage, setIsLoading },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey, selectedRange, appliedFilters, performRequest],
  );

  return { aggregates, coverage, isLoading };
};

type PerformRequest = ReturnType<typeof useApp>["performRequest"];

/**
 * One load of the aggregates for the current ids. The aggregates AND their
 * coverage are reset first — on every dependency change, an empty id list
 * and a failed read alike — so the page never merges a previous answer's
 * coverage into the current one (Codex confirmation review 7). Returns the
 * effect's cleanup.
 */
export const loadDeviceAggregates = (
  q: {
    deviceIds: string[];
    selectedRange: AnalyticsDateRange;
    appliedFilters: AnalyticsFilters;
    performRequest: PerformRequest;
    abortRef: { current: AbortController | null };
  },
  set: {
    setAggregates: (v: Record<string, DeviceAggregateRow>) => void;
    setCoverage: (v: Coverage | null) => void;
    setIsLoading: (v: boolean) => void;
  },
): (() => void) | undefined => {
  set.setAggregates({});
  set.setCoverage(null);
  if (q.deviceIds.length === 0) {
    set.setIsLoading(false);
    return undefined;
  }

  const controller = new AbortController();
  q.abortRef.current?.abort();
  q.abortRef.current = controller;

  const base = createDeviceListRequest(
    q.selectedRange,
    q.appliedFilters,
    1,
    q.deviceIds.length,
  );
  const payload = {
    deviceIds: q.deviceIds,
    filters: base.filters,
    createdAt: base.createdAt,
  };

  set.setIsLoading(true);
  q.performRequest<DeviceAggregatesResponse | null>("/devices/aggregates", {
    method: "POST",
    body: JSON.stringify(payload),
    signal: controller.signal,
  })
    .then((response) => {
      if (controller.signal.aborted) return;
      const next = deviceAggregatesOf(response);
      set.setAggregates(next.aggregates);
      set.setCoverage(next.coverage);
    })
    .catch((error: unknown) => {
      if (controller.signal.aborted) return;
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      set.setAggregates({});
      set.setCoverage(null);
      // eslint-disable-next-line no-console
      console.error("Failed to load device aggregates", error);
    })
    .finally(() => {
      if (!controller.signal.aborted) set.setIsLoading(false);
    });

  return () => {
    controller.abort();
  };
};
